'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { type FC, type ReactNode, useState } from 'react';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { BaseLineMore } from '@/app/components/icons/BaseLineMore';
import { ChannelBilibili } from '@/app/components/icons/ChannelBilibili';
import { ChannelDiscord2 } from '@/app/components/icons/ChannelDiscord2';
import { ChannelRed } from '@/app/components/icons/ChannelRed';
import { ChannelTt } from '@/app/components/icons/ChannelTt';
import { ChannelWechat3 } from '@/app/components/icons/ChannelWechat3';
import { ChannelX } from '@/app/components/icons/ChannelX';
import { ChannelYoutube } from '@/app/components/icons/ChannelYoutube';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import { PCNotifications } from '@/app/components/shared/NotificationCenter';
import { PCUserMenu } from '@/app/components/shared/UserCenter';
import { Popover, PopoverContent, PopoverTrigger } from '@/app/components/ui/popover';
import { API_URL, isEnterpriseDistribution, QRCODE_URL, SITE, TWITTER_LINK } from '@/app/constants';
import { useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { SubsPlanType } from '@/app/types';
import { cn, formatCount, getSubscriptionUrl, isPersonalSpaceId, reportEvent } from '@/app/utils';
import { useLocale, usePathname, useRouter } from '@/i18n/routing';
import { EarnCreditsIcon } from '../../Header/assets/EarnCreditsIcon';

const ICON_BUTTON_CLASSNAME =
  'flex h-[40px] w-[40px] mx-auto cursor-pointer items-center justify-center rounded-10 text-system-white64 transition-colors hover:bg-system-white08 hover:text-system-white';
const SOCIAL_LINK_CLASSNAME =
  'flex h-10 w-full cursor-pointer items-center justify-between rounded-8 p-2 text-system-white transition-colors hover:bg-system-white08';

// 会员类型文案配色，与 CreditsButton 保持一致（渐变版本靠 bg-clip-text 透出）
const CREDITS_PLAN_STYLE_MAP: Record<SubsPlanType, string> = {
  free: 'text-[#1AC3FF]',
  standard_v2: 'bg-[linear-gradient(270deg,#78A3FF_0%,#E5F2FF_100%)]',
  premium_v2: 'bg-[linear-gradient(270deg,#BA9D64_0%,#FFF7D0_100%)]',
  ultimate_v2: 'bg-[linear-gradient(90deg,#EAEBFF_0%,#877DFF_100%)]',
  team_v2: 'text-white',
};

const isChinaSite = SITE === 'china';
const WECHAT_LABEL = '微信群';
const DISCORD_LABEL = 'Discord';
const WECHAT_QR_TOOLTIP_CONTENT = (
  <div className="h-[248px] w-[216px] rounded-12 border border-system-white12 bg-system-bg02 px-2 py-3">
    <div className="mx-auto h-[200px] w-[200px]">
      <ImageWithPlaceholder src={QRCODE_URL} alt="微信群二维码" className="h-full w-full rounded-6 bg-[#3C4352]" />
    </div>
    <p className="mt-2 text-center font-[400] text-system-white64 text-xs tracking-[0]">加入讨论群，了解最新动态</p>
  </div>
);

const CN_SOCIAL_LINKS = isEnterpriseDistribution
  ? []
  : [
      ...(isChinaSite
        ? [
            {
              label: WECHAT_LABEL,
              displayLabel: WECHAT_LABEL,
              icon: <ChannelWechat3 className="text-xl" />,
              href: '#',
            },
          ]
        : []),
      {
        label: 'Douyin',
        displayLabel: '抖音',
        icon: <ChannelTt className="text-xl" />,
        href: 'https://v.douyin.com/iMA3xoFV/',
      },
      {
        label: 'Bilibili',
        displayLabel: '哔哩哔哩',
        icon: <ChannelBilibili className="text-xl" />,
        href: 'https://space.bilibili.com/3546707509906117',
      },
      {
        label: 'Xiaohongshu',
        displayLabel: '小红书',
        icon: <ChannelRed className="text-xl" />,
        href: 'https://www.xiaohongshu.com/user/profile/66277a1a000000000303295b?xhsshare=CopyLink&appuid=5e5f08880000000001000cf4&apptime=1721633528&share_id=7232bad910cf4b1b8819394c5f886296',
      },
    ];

const EN_SOCIAL_LINKS = [
  {
    label: DISCORD_LABEL,
    displayLabel: DISCORD_LABEL,
    icon: (
      <div className="rounded-6 bg-[#5865F2] p-[1px]">
        <ChannelDiscord2 className="text-lg text-white" />
      </div>
    ),
    href: 'https://discord.gg/Ymeg4kDbmF',
  },
  {
    label: 'X',
    displayLabel: 'X',
    icon: <ChannelX className="text-xl" />,
    href: TWITTER_LINK,
  },
  {
    label: 'YouTube',
    displayLabel: 'YouTube',
    icon: <ChannelYoutube className="text-xl" />,
    href: 'https://www.youtube.com/channel/UChF9964qVeqdSL9MkzBwCig',
  },
  {
    label: 'TikTok',
    displayLabel: 'TikTok',
    icon: <ChannelTt className="text-xl" />,
    href: 'https://www.tiktok.com/@vidu_ai?lang=en',
  },
];

const SOCIAL_LINKS = isChinaSite ? CN_SOCIAL_LINKS : EN_SOCIAL_LINKS;

const APP_PROMO_BANNER_URL =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/app-download-img-zh-d445e0b6.webp'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/app-download-img-en-95b71280.webp';

export const SidebarUserToolbar: FC = () => {
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [pcNotificationsPopoverOpen, setPcNotificationsPopoverOpen] = useState(false);
  const [pcUserMenuPopoverOpen, setPcUserMenuPopoverOpen] = useState(false);
  const [socialPopoverOpen, setSocialPopoverOpen] = useState(false);

  const { userInfo, teamSpaceInfo, isTeamSpace, isUserInfoInitialized, creditsInfo } = useStore((state) => ({
    userInfo: state.userInfo,
    teamSpaceInfo: state.teamSpaceInfo,
    isTeamSpace: state.isTeamSpace,
    isUserInfoInitialized: state.isUserInfoInitialized,
    creditsInfo: state.creditsInfo,
  }));

  const { open: openTaskCenterDrawer } = useGlobalModal('taskCenterDrawer');
  const { open: openDownloadAppDialog } = useGlobalModal('downloadAppDialog');
  const { open: openLoginDialog } = useGlobalModal('loginDialog');

  const handleOpenEarnCreditsDialog = () => {
    openTaskCenterDrawer({});
  };

  const isTeamSpaceSelected = !isPersonalSpaceId(userInfo?.internal?.space_id);

  // 用户信息初始化前，或团队空间信息尚未就绪时，先展示骨架，避免右侧内容闪动
  if (!isUserInfoInitialized || (isTeamSpaceSelected && !teamSpaceInfo)) {
    return (
      <div className="flex flex-col items-center gap-3">
        <div className="size-9 animate-pulse rounded-full bg-system-white08" />
        <div className="size-9 animate-pulse rounded-full bg-system-white08" />
      </div>
    );
  }

  const planType: SubsPlanType = (userInfo?.subs_plan as SubsPlanType) ?? 'free';

  const renderCredits = (variant: SubsPlanType) => (
    <CustomTooltip content={t('payments.subscription')} side="right" sideOffset={8}>
      <button
        type="button"
        className={cn(ICON_BUTTON_CLASSNAME, 'flex-col gap-0.5')}
        onClick={() => router.push(getSubscriptionUrl(isTeamSpace))}
      >
        <span
          className={cn(
            'bg-clip-text font-Poppins font-normal text-sm text-transparent leading-none',
            CREDITS_PLAN_STYLE_MAP[variant],
          )}
        >
          {creditsInfo === null ? '--' : formatCount(creditsInfo.total, locale)}
        </span>
        <span className={cn('bg-clip-text text-[10px] text-transparent leading-none', CREDITS_PLAN_STYLE_MAP[variant])}>
          {t(`payments.${variant}`)}
        </span>
      </button>
    </CustomTooltip>
  );

  const notifications = (
    <PCNotifications
      popoverOpen={pcNotificationsPopoverOpen}
      onPopoverOpenChange={setPcNotificationsPopoverOpen}
      triggerClassName={cn(ICON_BUTTON_CLASSNAME, 'bg-transparent')}
    />
  );
  const userMenu = (
    <PCUserMenu
      popoverOpen={pcUserMenuPopoverOpen}
      onPopoverOpenChange={setPcUserMenuPopoverOpen}
      triggerClassName={cn(ICON_BUTTON_CLASSNAME, 'bg-transparent')}
      avatarClassName="size-7"
    />
  );
  // 未登录时用登录入口占掉头像 + 额度的位置，下方图标区照常展示
  const loginButton = (
    <button
      type="button"
      className="flex h-[40px] w-full items-center justify-center overflow-hidden rounded-10 border border-system-white40 px-1 text-center font-normal text-system-white text-xs leading-[14px] transition-colors [word-break:break-word] hover:bg-system-white08"
      onClick={() => {
        openLoginDialog();
        reportEvent('login_pop_exposure', {
          refer_element: 'sidebar - sign in',
          refer_page: pathname,
        });
      }}
    >
      {t('header.navLogin')}
    </button>
  );

  // 社媒「更多」弹层内容
  const socialLinksContent: ReactNode = SOCIAL_LINKS.map(({ icon, href, label, displayLabel }) => {
    const socialLinkContent = (
      <>
        <div className="flex items-center gap-3">
          <span className="flex size-5 shrink-0 items-center justify-center text-system-white64">{icon}</span>
          <span className="font-normal text-sm leading-[1.4]">{displayLabel}</span>
        </div>
        <BaseLineLeft className="size-4 rotate-180 text-system-white24" />
      </>
    );

    if (label === WECHAT_LABEL) {
      return (
        <CustomTooltip
          key={label}
          content={WECHAT_QR_TOOLTIP_CONTENT}
          side="top"
          align="center"
          sideOffset={8}
          variant="extend"
          contentClassName="border-none bg-transparent p-0 shadow-none"
        >
          <button
            type="button"
            title={label}
            aria-label={label}
            className={SOCIAL_LINK_CLASSNAME}
            onClick={() => {
              reportEvent('element_click', { element_name: label });
            }}
          >
            {socialLinkContent}
          </button>
        </CustomTooltip>
      );
    }

    return (
      <ClientLink
        key={label}
        href={href}
        target="_blank"
        title={label}
        rel="nofollow noreferrer"
        className={SOCIAL_LINK_CLASSNAME}
        onClick={() => {
          reportEvent('element_click', { element_name: label });
        }}
      >
        {socialLinkContent}
      </ClientLink>
    );
  });

  const appPromoContent = (
    <div
      className="relative h-[118px] w-full cursor-pointer overflow-hidden rounded-t-8"
      onClick={() => {
        setSocialPopoverOpen(false);
        openDownloadAppDialog({ fromPage: 'window' });
        reportEvent('get_app_click', { click_position: 'sidebar_more' });
      }}
    >
      <Image
        src={APP_PROMO_BANNER_URL}
        alt="App Promo Banner"
        fill
        sizes="272px"
        className="object-cover object-bottom"
        aria-hidden="true"
      />
    </div>
  );

  const moreButton =
    SOCIAL_LINKS.length > 0 ? (
      <Popover open={socialPopoverOpen} onOpenChange={setSocialPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={t('sidebar.more')}
            title={t('sidebar.more')}
            className={ICON_BUTTON_CLASSNAME}
            onClick={() => reportEvent('element_click', { element_name: 'menu - more social' })}
          >
            <BaseLineMore className="text-xl" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="left"
          align="center"
          sideOffset={8}
          className="flex w-[280px] flex-col gap-1 rounded-12 border border-system-white08 bg-[rgba(34,37,42,0.64)] p-1 shadow-[0_4px_20px_rgba(0,0,0,0.4)] backdrop-blur-[30px] lg:w-[280px]"
          // 默认会把焦点移到内容里第一个 button（即「微信群」），Radix Tooltip 收到 focus 就会展开二维码
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          {appPromoContent}
          <div className="flex w-full flex-col">{socialLinksContent}</div>
        </PopoverContent>
      </Popover>
    ) : null;

  if (isTeamSpace) {
    return (
      <div className="flex flex-col gap-2">
        {userMenu}
        {renderCredits('team_v2')}
        {moreButton}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {userInfo ? (
        <>
          {userMenu}
          {renderCredits(planType)}
        </>
      ) : (
        loginButton
      )}
      <div className="flex flex-col gap-0">
        {userInfo && (
          <CustomTooltip content={t('freeCreditsDialog.earnCredits')} side="right" sideOffset={8}>
            <button type="button" className={ICON_BUTTON_CLASSNAME} onClick={handleOpenEarnCreditsDialog}>
              <EarnCreditsIcon className="text-xl" />
            </button>
          </CustomTooltip>
        )}
        {notifications}
        <CustomTooltip content={t('api-platform')} side="right" sideOffset={8}>
          <ClientLink
            href={API_URL}
            target="_blank"
            className={ICON_BUTTON_CLASSNAME}
            onClick={() => reportEvent('element_click', { element_name: 'API' })}
          >
            <span className="font-semibold text-xs">API</span>
          </ClientLink>
        </CustomTooltip>
        {moreButton}
      </div>
    </div>
  );
};
